import {
  Passenger,
  PassengerAddress,
  PassengerPhone,
  PassengerManualClient,
} from '../data/models/passenger.model';
import { ServicePlan } from '../data/models/service-plan.model';
import { phoneUtils } from './phoneUtils';
import { DateTime } from 'luxon';
import { MEMBER_PORTAL_NO_BENEFIT_PLAN_NAMES } from '../data/constants';

class PassengerUtils {
  private getDisplayAddress(address?: PassengerAddress): any {
    if (address?.addressLine1) {
      const main_text = `${address?.addressLine1}`;
      const secondary_text = `${address?.city}, ${address?.stateCode}, USA`;
      return {
        description: `${main_text}, ${secondary_text}`,
        zipCode: address?.zipCode || '',
      };
    }
    return '';
  }

  public getProfileDefaults(passenger?: Passenger): any {
    const mobile = phoneUtils.getPassengerMobilePhone(
      passenger?.passengerPhones || [],
    );
    const home = phoneUtils.getPassengerHomePhone(
      passenger?.passengerPhones || [],
    );
    const mobilePhone = mobile?.phoneNumber || '';
    const homePhone = home?.phoneNumber || '';
    const servicePlans =
      passenger?.servicePlans?.map((sp) => sp.servicePlanUUID) || [];
    const manual: any = passenger?.manual;
    const manualAddress = passenger?.passengerAddresses?.find(
      (addr) => addr.passengerAddressUseBC === 'MANUAL',
    );
    const addressToDisplay =
      manualAddress || passenger?.passengerAddresses?.[0];
    const passengerData = {
      PassengerUUID: passenger?.passengerUUID,
      Client: passenger?.clientBC || '',
      FirstName: passenger?.firstName || '',
      MiddleName: passenger?.middleName || '',
      LastName: passenger?.lastName || '',
      PassengerID: passenger?.clientPassengerID || '',
      DOB: passenger?.dateOfBirth
        ? DateTime.fromISO(passenger.dateOfBirth)
        : null,
      Gender: passenger?.genderCode || '',
      Address: this.getDisplayAddress(addressToDisplay),
      AddressStruct: addressToDisplay
        ? JSON.stringify(addressToDisplay)
        : undefined,
      AddressLine2: addressToDisplay?.addressLine2 || '',
      Phone: homePhone,
      PhoneExtension: home?.phoneExtension || '',
      Mobile: mobilePhone,
      MobileExtension: mobile?.phoneExtension || '',
      SMSConsent: mobile?.hasSMSConsent || false,
      Email: passenger?.passengerEmailAddress || '',
      ServicePlans: servicePlans,
      AuthorizedFirstName: manual?.authorizationByClientFirstName || '',
      AuthorizedLastName: manual?.authorizationByClientLastName || '',
      CreateReason: manual?.reasonBC || '',
    };
    return passengerData;
  }

  public getPassengerFromFormData(
    formData: any,
    existingPassenger?: Passenger,
  ): Passenger {
    const homePhone = this.getPhone(formData, 'Phone');
    const mobilePhone = this.getPhone(formData, 'Mobile');
    const address = this.getAddress(formData);
    const servicePlans = formData.ServicePlans.map((spid: string) => ({
      servicePlanUUID: spid,
      effectiveStartDate: DateTime.now().startOf('day').toISODate(),
    }));
    const manual = this.getManual(formData);
    const passenger: Passenger = {
      passengerUUID: formData.PassengerUUID,
      clientBC: formData.Client,
      firstName: formData.FirstName,
      middleName: formData.MiddleName || undefined,
      lastName: formData.LastName,
      clientPassengerID: formData.PassengerID,
      dateOfBirth: formData.DOB.toISODate(),
      genderCode: formData.Gender || undefined,
      passengerEmailAddress: formData.Email || undefined,
      passengerPhones: [],
      passengerAddresses: [],
      servicePlans,
      manual,
    };

    if (
      existingPassenger?.passengerPhones &&
      existingPassenger.passengerPhones.length > 0
    ) {
      const existingNonManualPhones = existingPassenger.passengerPhones.filter(
        (phone) => phone.passengerPhoneUseBC !== 'MANUAL',
      );
      passenger.passengerPhones = [...existingNonManualPhones];
    }

    if (homePhone) {
      const existingManualLandlineIndex = passenger.passengerPhones.findIndex(
        (phone) =>
          phone.passengerPhoneUseBC === 'MANUAL' &&
          phone.phoneNumberTypeBC === 'LANDLINE',
      );

      if (existingManualLandlineIndex >= 0) {
        passenger.passengerPhones[existingManualLandlineIndex] = homePhone;
      } else {
        passenger.passengerPhones.push(homePhone);
      }
    }

    if (mobilePhone) {
      const existingManualMobileIndex = passenger.passengerPhones.findIndex(
        (phone) =>
          phone.passengerPhoneUseBC === 'MANUAL' &&
          phone.phoneNumberTypeBC === 'MOBILE',
      );

      if (existingManualMobileIndex >= 0) {
        passenger.passengerPhones[existingManualMobileIndex] = mobilePhone;
      } else {
        passenger.passengerPhones.push(mobilePhone);
      }
    }

    if (
      existingPassenger?.passengerAddresses &&
      existingPassenger.passengerAddresses.length > 0
    ) {
      passenger.passengerAddresses = [...existingPassenger.passengerAddresses];

      if (address) {
        const manualAddressIndex = passenger.passengerAddresses.findIndex(
          (addr) => addr.passengerAddressUseBC === 'MANUAL',
        );

        if (manualAddressIndex >= 0) {
          passenger.passengerAddresses[manualAddressIndex] = address;

          if (manualAddressIndex !== 0) {
            passenger.passengerAddresses.splice(manualAddressIndex, 1);
            passenger.passengerAddresses.unshift(address);
          }
        } else {
          passenger.passengerAddresses.unshift(address);
        }
      } else {
        const manualAddressIndex = passenger.passengerAddresses.findIndex(
          (addr) => addr.passengerAddressUseBC === 'MANUAL',
        );

        if (manualAddressIndex >= 0) {
          passenger.passengerAddresses.splice(manualAddressIndex, 1);
        }
      }
    } else {
      if (address) {
        passenger.passengerAddresses.push(address);
      }
    }

    return passenger;
  }

  private getAddress(formData: any): PassengerAddress | undefined {
    let address: PassengerAddress | undefined = undefined;
    const addr = JSON.parse(formData.AddressStruct || '{}');
    if (addr.addressLine1 && addr.city) {
      address = {
        addressLine1: addr.addressLine1,
        addressLine2: formData.AddressLine2 || undefined,
        city: addr.city,
        stateCode: addr.stateCode,
        zipCode: addr.zipCode,
        passengerAddressUseBC: 'MANUAL',
        label: 'Manual Entry',
      };
    }
    return address;
  }

  private getPhone(
    formData: any,
    name: 'Phone' | 'Mobile',
  ): PassengerPhone | undefined {
    let phone: PassengerPhone | undefined = undefined;
    const ph = formData[name];
    const isMobile = name !== 'Phone';
    if (ph) {
      let phoneExtKey = `${name}Extension`;
      phone = {
        phoneNumber: `${ph.replace(/\s/g, '')}`,
        hasSMSConsent: isMobile ? formData.SMSConsent : false,
        phoneNumberTypeBC: isMobile ? 'MOBILE' : 'LANDLINE',
        passengerPhoneUseBC: 'MANUAL',
        phoneExtension: formData[phoneExtKey],
      };
    }

    return phone;
  }

  private getManual(formData: any): PassengerManualClient {
    const manual: PassengerManualClient = {
      authorizationByClientFirstName: formData.AuthorizedFirstName,
      authorizationByClientLastName: formData.AuthorizedLastName,
      passengerAuthorizationReasonBC: formData.CreateReason,
    };
    return manual;
  }

  public showNoBenefitBanner(plans: ServicePlan[]): boolean {
    // Number of plans
    const plansCount = plans.length;

    // No plans at all
    if (plansCount < 1) {
      return true;
    }

    // How many plans falls under MEMBER_PORTAL_NO_BENEFIT_PLAN_NAMES
    const noBenefitPlansCount = plans.filter(
      (plan) =>
        plan.servicePlanName &&
        MEMBER_PORTAL_NO_BENEFIT_PLAN_NAMES.includes(plan.servicePlanName),
    ).length;

    // Is all plans are No Benefit Plans
    if (noBenefitPlansCount === plansCount) {
      return true;
    }

    return false;
  }
}

const passengerUtils = new PassengerUtils();

export default passengerUtils;
